import UpdateBanner from './UpdateBanner';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { 
  LayoutDashboard, 
  Box,
  FileText,
  ShoppingCart,
  MessageSquare,
  User,
  LogOut,
  Menu,
  X,
  Settings
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import UserSettingsDialog from './UserSettingsDialog';
import { useTranslation } from 'react-i18next';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'My Products', href: '/products', icon: Box },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Order New', href: '/order', icon: ShoppingCart },
  { name: 'Support', href: '/tickets', icon: MessageSquare },
  { name: 'Profile', href: '/profile', icon: User },
];

export default function Layout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { customer, logout, refreshToken } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    if (refreshToken) {
      try {
        await fetch('/api/v1/customer/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Ignore errors
      }
    }
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600/75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-brand-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between px-6">
          <img src={`${import.meta.env.BASE_URL}logo-text-light.png`} alt="tucorreoypagina.com" className="h-8" />
          <button
            className="lg:hidden text-brand-200 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-3 space-y-1">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-800 text-white'
                    : 'text-brand-200 hover:bg-brand-800 hover:text-white'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-brand-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {customer?.firstName || customer?.email}
              </p>
              <p className="text-xs text-brand-300 truncate">{customer?.company || 'Customer'}</p>
            </div>
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-2 text-brand-300 hover:text-white rounded-lg hover:bg-brand-800"
              title={t('nav.settings')}
            >
              <Settings className="h-5 w-5" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 text-brand-300 hover:text-white rounded-lg hover:bg-brand-800"
              title={t('nav.logout')}
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-8">
          <button
            className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
          <UpdateBanner />
        </main>
      </div>

      <UserSettingsDialog
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        user={customer}
      />
    </div>
  );
}
